import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { User, Smartphone, KeyRound } from 'lucide-react';
import GoogleSignInButton from '../GoogleSignInButton';
import VendorGoogleFinalizeForm from './VendorGoogleFinalizeForm';

/**
 * Vendor login card — handles OTP / password / forgot-password / Google sign-in
 * across all sub-steps. Stateless: parent owns form state and submit handlers.
 */
const VendorLoginCard = ({
  // Customer-session banner
  customerSession,
  onSignOutCustomer,
  // Google profile completion flow
  googleProfile,
  setGoogleProfile,
  submitGoogleProfile,
  gpSubmitting,
  handleGoogleVendor,
  // Login step state
  step,
  setStep,
  loginTab,
  setLoginTab,
  // Mobile + OTP + password
  mobile,
  setMobile,
  otp,
  setOtp,
  password,
  setPassword,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  devOtp,
  submitting,
  // Handlers
  sendOtp,
  verifyOtp,
  loginWithPassword,
  startForgotPassword,
  submitForgotPassword,
}) => (
  <div className="max-w-md mx-auto px-4 py-12">
    {customerSession && !googleProfile && (
      <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3 text-xs text-amber-900" data-testid="vendor-customer-session-banner">
        You&apos;re signed in as <b>{customerSession.name}</b> (customer account: {customerSession.email}).
        Clicking <b>Sign in with Google</b> below will register / log in <i>the same email</i> as a vendor.
        <button
          type="button"
          onClick={onSignOutCustomer}
          className="text-[#2c54b2] underline ml-1 hover:text-[#ee5e23]"
          data-testid="vendor-logout-customer-link"
        >
          Sign out of customer first
        </button>
      </div>
    )}
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h1 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
        <User className="w-5 h-5 text-[#ee5e23]" /> Vendor Login
      </h1>
      <p className="text-sm text-gray-600 mb-4">Sign in to manage your products and leads.</p>

      {googleProfile ? (
        <VendorGoogleFinalizeForm
          profile={googleProfile}
          onChange={setGoogleProfile}
          onSubmit={submitGoogleProfile}
          onCancel={() => setGoogleProfile(null)}
          submitting={gpSubmitting}
        />
      ) : step === 1 ? (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Mobile Number</Label>
            <div className="flex gap-2 mt-1">
              <div className="flex items-center px-3 border rounded-md bg-gray-50 text-sm text-gray-700">+91</div>
              <Input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" data-testid="vendor-mobile-input" />
            </div>
          </div>

          <div className="flex border-b border-gray-200">
            <button type="button" onClick={() => setLoginTab('otp')} className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${loginTab === 'otp' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600'}`} data-testid="vendor-login-tab-otp">
              <Smartphone className="w-4 h-4 inline mr-1" /> OTP Login
            </button>
            <button type="button" onClick={() => setLoginTab('password')} className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${loginTab === 'password' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600'}`} data-testid="vendor-login-tab-password">
              <KeyRound className="w-4 h-4 inline mr-1" /> Password
            </button>
          </div>

          {loginTab === 'otp' ? (
            <Button onClick={sendOtp} disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11" data-testid="vendor-send-otp-btn">
              <Smartphone className="w-4 h-4 mr-2" /> {submitting ? 'Sending...' : 'Send OTP'}
            </Button>
          ) : (
            <form onSubmit={loginWithPassword} className="space-y-3">
              <div>
                <Label className="text-xs">Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" autoComplete="current-password" data-testid="vendor-password-input" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11" data-testid="vendor-password-login-btn">
                <KeyRound className="w-4 h-4 mr-2" /> {submitting ? 'Signing in…' : 'Sign In'}
              </Button>
              <button type="button" onClick={startForgotPassword} className="text-xs text-[#2c54b2] hover:underline w-full" data-testid="vendor-forgot-password-link">
                Forgot password? Reset via OTP
              </button>
            </form>
          )}

          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-500">or</span></div>
          </div>
          <GoogleSignInButton onCredential={handleGoogleVendor} />
          <p className="text-xs text-center text-gray-500">
            Not a vendor yet? <Link to="/vendor/register" className="text-[#2c54b2] hover:underline">Register here</Link>
          </p>
        </div>
      ) : step === 'forgot' ? (
        <form onSubmit={submitForgotPassword} className="space-y-3" data-testid="vendor-forgot-form">
          <div>
            <Label className="text-xs">OTP sent to +91 {mobile}</Label>
            <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="4-digit OTP" className="text-center tracking-widest text-lg" data-testid="vendor-fp-otp" />
          </div>
          {devOtp && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Dev OTP: <b>{devOtp}</b></div>}
          <div>
            <Label className="text-xs">New Password</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 chars, letters + numbers" autoComplete="new-password" data-testid="vendor-fp-newpass" />
          </div>
          <div>
            <Label className="text-xs">Confirm Password</Label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password" data-testid="vendor-fp-confirm" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11" data-testid="vendor-fp-submit">
            {submitting ? 'Resetting…' : 'Reset password & sign in'}
          </Button>
          <button type="button" onClick={() => { setStep(1); setOtp(''); setNewPassword(''); setConfirmPassword(''); }} className="text-xs text-[#2c54b2] hover:underline w-full">Back to sign in</button>
        </form>
      ) : (
        <form onSubmit={verifyOtp} className="space-y-3">
          <div>
            <Label className="text-xs">Enter OTP for +91 {mobile}</Label>
            <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center tracking-widest text-lg" placeholder="4-digit OTP" />
          </div>
          {devOtp && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Dev mode: <b>{devOtp}</b></div>}
          <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11">
            <KeyRound className="w-4 h-4 mr-2" /> {submitting ? 'Verifying...' : 'Login'}
          </Button>
          <button type="button" onClick={() => setStep(1)} className="text-xs text-[#2c54b2] hover:underline w-full">Change number</button>
        </form>
      )}
    </div>
  </div>
);

export default VendorLoginCard;
